/**
 * services/sync/GoogleDriveSyncProvider.ts
 * Google Drive implementation of OAuth cloud sync provider
 *
 * Provides cloud sync to Google Drive using BFF (Backend-for-Frontend) pattern
 * with Supabase Edge Functions. OAuth token exchange and Drive API calls are
 * handled server-side for enhanced security.
 *
 * **Security Architecture (BFF Pattern)**:
 * - OAuth flow initiated via `google-oauth-start` Edge Function
 * - Token exchange handled by `google-oauth-callback` Edge Function
 * - All Drive API calls proxied through `google-drive-proxy` Edge Function
 * - Client secret never exposed to browser
 * - Tokens encrypted at rest in Supabase database
 */

import { getGoogleDriveConfig } from '@/config/oauth-providers';
import type { SyncProviderType, SyncFileInfo, SyncErrorCode } from '@/types/sync/SyncProvider';
import { SyncProviderEnum } from '@/types/sync/SyncProvider';
import type { OAuthConfig } from '@/types/sync/OAuthConfig';
import { OAuthCloudSyncProvider } from './OAuthCloudSyncProvider';
import { supabase } from '@/lib/supabase';

/**
 * Google Drive sync provider using BFF (Backend-for-Frontend) pattern
 *
 * Extends OAuthCloudSyncProvider to implement Google Drive-specific file operations.
 * All sync files are stored in a dedicated application folder with
 * restricted scope (only files created by this app are accessible).
 *
 * **Key Features**:
 * - OAuth2 PKCE authentication (no client secret required)
 * - Automatic token refresh
 * - File ID caching for efficient operations
 * - Multipart uploads for new files
 * - Media uploads for file updates
 *
 * @example
 * ```typescript
 * const provider = new GoogleDriveSyncProvider();
 *
 * // Connect with OAuth (opens popup)
 * await provider.connect();
 *
 * // Upload sync file
 * await provider.write('baseline_20250110.json', jsonData);
 *
 * // List all sync files
 * const files = await provider.list();
 *
 * // Download sync file
 * const data = await provider.read('baseline_20250110.json');
 * ```
 */
export class GoogleDriveSyncProvider extends OAuthCloudSyncProvider {
  /** Provider identifier */
  readonly name: SyncProviderType = SyncProviderEnum.GoogleDrive as SyncProviderType;

  /** Indicates this provider supports binary file operations */
  readonly supportsBinary = true;

  /** Cache for filename → Drive file ID mapping (for efficient updates/deletes) */
  private fileIdCache: Map<string, string> = new Map();

  /** Connection status cache (BFF pattern - tokens are server-side) */
  private connectedCache: boolean | null = null;
  private connectedCacheTs = 0;
  private static readonly CONNECTED_TTL_MS = 30_000; // 30s cache to avoid extra roundtrips

  // ===== BFF PATTERN OVERRIDES =====

  /**
   * Check if user has valid server-side tokens for Google Drive
   *
   * In BFF pattern, tokens are stored server-side in Supabase database.
   * This method pings the proxy to check if tokens exist for the current user.
   * Results are cached for 30 seconds to avoid excessive API calls.
   *
   * @returns Promise resolving to true if connected (tokens exist server-side)
   */
  public override async isConnected(): Promise<boolean> {
    // Fast path: use cached result if within TTL
    const now = Date.now();
    const cacheAge = now - this.connectedCacheTs;
    if (
      this.connectedCache !== null &&
      cacheAge < GoogleDriveSyncProvider.CONNECTED_TTL_MS
    ) {
      console.log(`[GoogleDriveSyncProvider.isConnected] Returning cached value: ${this.connectedCache} (age: ${cacheAge}ms)`);
      return this.connectedCache;
    }

    console.log(`[GoogleDriveSyncProvider.isConnected] Cache miss/expired, calling BFF... (cache: ${this.connectedCache}, age: ${cacheAge}ms)`);
    try {
      // Check server-side token existence via proxy
      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: { operation: 'check-connection' },
      });

      if (error) {
        // Don't cache 401 errors - they're transient auth timing issues on first load
        // These are expected and handled gracefully - no need to log
        console.log(`[GoogleDriveSyncProvider.isConnected] BFF error, returning false:`, error);
        return false;
      }

      const connected = data?.connected === true;
      this.connectedCache = connected;
      this.connectedCacheTs = now;
      console.log(`[GoogleDriveSyncProvider.isConnected] BFF returned: ${connected}, updated cache`);
      return connected;
    } catch (_error) {
      // Network errors = disconnected (don't cache - might be transient)
      console.log(`[GoogleDriveSyncProvider.isConnected] Network error, returning false:`, _error);
      return false;
    }
  }

  /**
   * Ensure user has valid server-side tokens before Drive API operations
   *
   * In BFF pattern, "valid token" means server-side tokens exist for this user.
   * This method checks connection status via `isConnected()` rather than
   * loading tokens from IndexedDB (parent class behavior).
   *
   * The proxy handles token refresh automatically, so we only need to verify
   * that tokens exist - not that they're currently valid.
   *
   * @throws SyncError with NOT_CONNECTED code if no server-side tokens found
   */
  protected override async ensureValidToken(): Promise<void> {
    const connected = await this.isConnected();
    if (!connected) {
      // Invalidate connection cache on auth failure
      this.connectedCache = null;
      this.connectedCacheTs = 0;

      throw this.createSyncError(
        new Error('Not authenticated'),
        'NOT_CONNECTED',
        'No server-side tokens found. Please connect Google Drive first.'
      );
    }
    // Tokens are server-side; proxy handles access/refresh internally
  }

  // ===== OAUTH CONFIGURATION =====

  /**
   * Get Google Drive OAuth2 configuration
   *
   * Returns OAuth config with Google-specific endpoints, scopes, and client ID.
   * Configuration is loaded from environment variables via oauth-providers.ts.
   *
   * @returns Google Drive OAuth2 configuration
   */
  protected getOAuthConfig(): OAuthConfig {
    return getGoogleDriveConfig();
  }

  /**
   * Override OAuth flow to use BFF (Backend-for-Frontend) pattern
   *
   * Instead of handling OAuth in the browser, delegates to Supabase Edge Functions:
   * 1. Call `google-oauth-start` to get authorization URL
   * 2. Open popup for user consent
   * 3. `google-oauth-callback` handles token exchange server-side
   * 4. Redirect back to app with success/error
   *
   * IMPORTANT: To avoid browser popup blocking, the popup should be pre-opened
   * by the caller (synchronously in user gesture handler) and passed via options.oauthPopup.
   * If not provided, this method will attempt to open a popup (which may be blocked).
   *
   * @param options - Optional connection options (e.g., promptMode, oauthPopup)
   * @returns Promise resolving to true if authentication successful
   */
  protected override async startOAuthFlow(options?: import('@/types/sync/SyncProvider').ProviderConnectOptions): Promise<boolean> {
    // Use pre-opened popup if provided, otherwise try to open one (may be blocked)
    // Pre-opened popup is passed from modal handler where user gesture is fresh
    let popup = options?.oauthPopup;

    if (!popup || popup.closed) {
      // Fallback: try to open popup here (may be blocked if called after async work)
      console.warn('No pre-opened popup provided, attempting to open (may be blocked)...');
      popup = window.open('about:blank', 'GoogleOAuth', 'width=600,height=700');
    }

    if (!popup) {
      console.error('OAuth popup blocked by browser. Please allow popups for this site.');
      return false;
    }

    try {
      console.log('🔐 Starting BFF OAuth flow via Edge Functions...');

      // Build request body with optional promptMode
      const requestBody: { redirectUri: string; promptMode?: string } = {
        redirectUri: `${window.location.origin}/settings/data-sync`,
      };

      // Add promptMode if specified (Google account chooser)
      if (options?.promptMode) {
        requestBody.promptMode = options.promptMode;
      }

      // Call Edge Function to start OAuth and get auth URL
      const { data, error } = await supabase.functions.invoke('google-oauth-start', {
        body: requestBody,
      });

      if (error) {
        console.error('Failed to start OAuth flow:', error);
        popup.close();
        return false;
      }

      if (!data || !data.authUrl) {
        console.error('No auth URL returned from Edge Function');
        popup.close();
        return false;
      }

      console.log('✅ Got auth URL from Edge Function, navigating popup...');

      // Navigate the already-open popup to the auth URL
      popup.location.href = data.authUrl;

      // Wait for callback - monitor redirect back to our app
      const success = await this.monitorOAuthCallback(popup);

      if (success) {
        console.log('✅ OAuth authentication successful (BFF)');
        // Tokens are now stored server-side - update cache immediately
        this.connectedCache = true;
        this.connectedCacheTs = Date.now();
        return true;
      }

      return false;
    } catch (error) {
      // Rethrow errors that need targeted UI handling so hook can display appropriate messaging
      if (error instanceof Error && (
        error.message === 'USER_CANCELLED' ||
        error.message === 'ACCESS_DENIED' ||
        error.message === 'INSUFFICIENT_SCOPES' ||
        error.message === 'PROVIDER_OUTAGE'
      )) {
        popup.close();
        throw error;
      }
      console.error('BFF OAuth flow failed:', error);
      popup.close();
      return false;
    }
  }

  /**
   * Monitor OAuth popup for callback redirect
   *
   * Waits for popup to redirect back to app with success or error parameter.
   *
   * @param popup - Popup window reference
   * @returns Promise resolving to true if successful
   */
  private async monitorOAuthCallback(popup: Window): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        try {
          // Check if popup closed by user (before OAuth completed)
          if (popup.closed) {
            clearInterval(checkInterval);
            console.log('OAuth popup closed by user');
            // Reject with USER_CANCELLED so hook can distinguish from OAuth errors
            reject(new Error('USER_CANCELLED'));
            return;
          }

          // Try to read popup URL (will throw if cross-origin)
          const popupUrl = popup.location.href;

          // Check if we've been redirected back to our app
          if (popupUrl.includes(window.location.origin)) {
            const url = new URL(popupUrl);
            const oauthSuccess = url.searchParams.get('oauth_success');
            const oauthError = url.searchParams.get('oauth_error');

            if (oauthSuccess === 'true') {
              clearInterval(checkInterval);
              popup.close();
              console.log('✅ OAuth callback successful');
              resolve(true);
              return;
            }

            if (oauthError) {
              clearInterval(checkInterval);
              popup.close();
              console.error('OAuth callback error:', oauthError);
              // Map specific OAuth errors to distinct reasons for targeted UI messaging
              if (oauthError === 'access_denied') {
                // User clicked "Deny" on Google consent screen
                reject(new Error('ACCESS_DENIED'));
                return;
              }
              if (oauthError === 'insufficient_scopes') {
                // User denied file access scope (granular consent)
                reject(new Error('INSUFFICIENT_SCOPES'));
                return;
              }
              if (oauthError === 'server_error') {
                // Google server error (outage, temporary issue)
                reject(new Error('PROVIDER_OUTAGE'));
                return;
              }
              // Other unrecognized OAuth errors - fall through to generic failure
              resolve(false);
              return;
            }
          }
        } catch (_error) {
          // Cross-origin error - popup is still on Google's domain
          // This is expected, keep waiting
        }
      }, 500);

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!popup.closed) {
          popup.close();
        }
        console.error('OAuth flow timed out');
        resolve(false);
      }, 5 * 60 * 1000);
    });
  }

  // ===== FOLDER MANAGEMENT (BFF Pattern with Server-Side Persistence) =====

  /**
   * Create or locate app-specific folder in Google Drive (legacy signature for parent class)
   *
   * This method is required by the parent OAuthCloudSyncProvider class.
   * It delegates to ensureAppFolder() which handles server-side folder persistence.
   *
   * @returns Promise resolving to Google Drive folder ID
   * @throws SyncError if folder creation/search fails
   */
  protected async createAppFolder(): Promise<string> {
    return this.ensureAppFolder();
  }

  /**
   * Ensure app folder exists and is initialized (BFF pattern with lazy loading)
   *
   * Server-side operation handles folder creation/discovery with race-proof advisory locks.
   * Folder ID is persisted in database and loaded on-demand. Results are cached in memory
   * for the lifetime of the provider instance.
   *
   * This method is called before all file operations to ensure folder ID is available.
   *
   * @returns Promise resolving to Google Drive folder ID
   * @throws SyncError if folder initialization fails
   */
  protected async ensureAppFolder(): Promise<string> {
    // Use cached value if available
    if (this.appFolderId) {
      return this.appFolderId;
    }

    try {
      console.log('📁 Ensuring app folder exists...');

      // Call BFF to ensure folder exists (creates if needed, race-proof)
      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: { operation: 'ensure-app-folder' },
      });

      if (error) {
        throw this.createSyncError(
          error,
          'UNKNOWN_ERROR',
          'Failed to initialize app folder'
        );
      }

      // Check for standardized error response
      if (data && !data.ok) {
        throw this.createSyncError(
          new Error(data.message || 'Folder initialization failed'),
          data.code || 'UNKNOWN_ERROR',
          data.message || 'App folder initialization returned error'
        );
      }

      if (!data?.folderId) {
        throw this.createSyncError(
          new Error('Invalid response from ensure-app-folder'),
          'UNKNOWN_ERROR',
          'App folder initialization returned invalid response'
        );
      }

      // Cache for session
      const folderId: string = data.folderId;
      this.appFolderId = folderId;
      console.log(`✅ App folder initialized: ${folderId}`);

      return folderId;
    } catch (error) {
      console.error('Failed to ensure app folder:', error);

      // Re-throw SyncError as-is
      if (typeof error === 'object' && error !== null && 'code' in error) {
        throw error;
      }

      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        'Failed to ensure app folder exists'
      );
    }
  }

  /**
   * Self-healing wrapper for file operations
   *
   * Automatically retries operation once if folder-related error occurs.
   * Clears cached folder ID and connection status on error, allowing
   * ensureAppFolder() to re-initialize.
   *
   * @param operation - Async operation to execute
   * @param operationName - Name for logging
   * @returns Promise resolving to operation result
   * @throws SyncError if operation fails after retry
   */
  private async withSelfHeal<T>(
    operation: () => Promise<T>,
    operationName: string
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      // Check if error is folder-related (404/403 from Drive or FOLDER_MISSING)
      if (this.isFolderError(error)) {
        console.log(`${operationName} failed with folder error, attempting self-heal...`);

        // Clear cached folder ID
        this.appFolderId = null;

        // Also invalidate connection cache (force fresh check)
        this.connectedCache = null;
        this.connectedCacheTs = 0;

        // Retry operation once (will call ensureAppFolder again)
        try {
          return await operation();
        } catch (retryError) {
          // Retry failed - this is a real error, not just stale folder
          console.error(`${operationName} failed after self-heal attempt`);
          throw this.createSyncError(
            retryError,
            'UNKNOWN_ERROR',
            `${operationName} failed after self-heal attempt`
          );
        }
      }

      // Not a folder error - rethrow original
      throw error;
    }
  }

  /**
   * Check if error indicates folder missing or inaccessible
   *
   * @param error - Error to check
   * @returns True if error is folder-related
   */
  private isFolderError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const errorObj = error as { code?: string | number; message?: string };
    const code = errorObj.code;
    const message = errorObj.message?.toLowerCase() || '';

    return (
      code === 'FOLDER_MISSING' ||
      code === 404 ||
      code === 403 ||
      message.includes('folder') && (message.includes('not found') || message.includes('missing'))
    );
  }

  // ===== FILE OPERATIONS =====

  /**
   * Upload file to Google Drive app folder (with self-healing)
   *
   * Creates new file if it doesn't exist, updates if it does.
   * Uses multipart upload for new files (metadata + content),
   * media upload for updates (content only).
   *
   * Automatically ensures folder exists and retries if folder errors occur.
   *
   * @param filename - Name of file to upload
   * @param content - File contents as string (typically JSON)
   * @returns Promise resolving when upload completes
   * @throws SyncError if upload fails
   */
  protected async uploadFile(filename: string, content: string): Promise<void> {
    return this.withSelfHeal(async () => {
      await this.ensureValidToken();
      const folderId = await this.ensureAppFolder();

      console.log(`📤 Uploading file: ${filename}`);

      // Check if file already exists
      const existingFileId = await this.findFileByName(filename);

      if (existingFileId) {
        // Update existing file
        console.log(`🔄 Updating existing file: ${existingFileId}`);
        await this.updateFile(existingFileId, content);
        this.fileIdCache.set(filename, existingFileId);
      } else {
        // Create new file
        console.log(`🆕 Creating new file: ${filename}`);
        const newFileId = await this.createFile(filename, content, folderId);
        this.fileIdCache.set(filename, newFileId);
      }

      console.log(`✅ Uploaded file: ${filename}`);
    }, 'uploadFile');
  }

  /**
   * Download file from Google Drive app folder (with self-healing)
   *
   * @param filename - Name of file to download
   * @returns Promise resolving to file contents as string
   * @throws SyncError if file not found or download fails
   */
  protected async downloadFile(filename: string): Promise<string> {
    return this.withSelfHeal(async () => {
      await this.ensureValidToken();
      await this.ensureAppFolder(); // Ensure folder initialized

      console.log(`📥 Downloading file: ${filename}`);

      // Find file ID
      const fileId = await this.findFileByName(filename);

      if (!fileId) {
        throw this.createSyncError(
          new Error('File not found'),
          'FILE_NOT_FOUND',
          `File not found in Google Drive: ${filename}`
        );
      }

      // Download via Edge Function proxy
      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: {
          operation: 'download-file',
          params: { fileId },
        },
      });

      if (error) {
        if (error.message?.includes('404') || error.message?.includes('not found')) {
          throw this.createSyncError(
            error,
            'FILE_NOT_FOUND',
            `File not found: ${filename}`
          );
        }

        throw this.createSyncError(
          error,
          'UNKNOWN_ERROR',
          `Failed to download file: ${error.message}`
        );
      }

      const content = typeof data === 'string' ? data : JSON.stringify(data);
      console.log(`✅ Downloaded file: ${filename} (${content.length} bytes)`);

      return content;
    }, 'downloadFile');
  }

  /**
   * List all files in Google Drive app folder (with self-healing)
   *
   * Returns metadata for all sync files (baselines, deltas, manifest).
   * Populates file ID cache for efficient subsequent operations.
   *
   * @returns Promise resolving to array of file metadata
   * @throws SyncError if listing fails
   */
  protected async listFiles(): Promise<SyncFileInfo[]> {
    return this.withSelfHeal(async () => {
      await this.ensureValidToken();
      const folderId = await this.ensureAppFolder();

      console.log(`📋 Listing files in app folder...`);

      // List files via Edge Function proxy
      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: {
          operation: 'list-files',
          params: { folderId },
        },
      });

      if (error) {
        throw this.createSyncError(
          error,
          'UNKNOWN_ERROR',
          `Failed to list files: ${error.message}`
        );
      }

      // Map Drive files to SyncFileInfo and update cache
      const syncFiles: SyncFileInfo[] = [];

      if (data && data.files) {
        for (const file of data.files) {
          // Update cache
          this.fileIdCache.set(file.name, file.id);

          // Map to SyncFileInfo
          syncFiles.push({
            filename: file.name,
            size: file.size ? parseInt(file.size, 10) : 0,
            lastModified: file.modifiedTime || new Date().toISOString(),
          });
        }
      }

      console.log(`✅ Found ${syncFiles.length} files in app folder`);
      return syncFiles;
    }, 'listFiles');
  }

  /**
   * Delete file from Google Drive app folder (with self-healing)
   *
   * Permanently deletes file (bypasses trash).
   *
   * @param filename - Name of file to delete
   * @returns Promise resolving when deletion completes
   * @throws SyncError if file not found or deletion fails
   */
  protected async deleteFile(filename: string): Promise<void> {
    return this.withSelfHeal(async () => {
      await this.ensureValidToken();
      await this.ensureAppFolder(); // Ensure folder initialized

      console.log(`🗑️ Deleting file: ${filename}`);

      // Find file ID
      const fileId = await this.findFileByName(filename);

      if (!fileId) {
        throw this.createSyncError(
          new Error('File not found'),
          'FILE_NOT_FOUND',
          `File not found in Google Drive: ${filename}`
        );
      }

      // Delete via Edge Function proxy
      const { error } = await supabase.functions.invoke('google-drive-proxy', {
        body: {
          operation: 'delete-file',
          params: { fileId },
        },
      });

      if (error) {
        if (error.message?.includes('404') || error.message?.includes('not found')) {
          throw this.createSyncError(
            error,
            'FILE_NOT_FOUND',
            `File not found: ${filename}`
          );
        }

        throw this.createSyncError(
          error,
          'UNKNOWN_ERROR',
          `Failed to delete file: ${error.message}`
        );
      }

      // Remove from cache
      this.fileIdCache.delete(filename);

      console.log(`✅ Deleted file: ${filename}`);
    }, 'deleteFile');
  }

  // ===== HELPER METHODS =====

  /**
   * Find file by name in app folder
   *
   * Checks cache first, then searches Drive API if not cached.
   *
   * @param filename - Name of file to find
   * @returns Promise resolving to file ID if found, null if not found
   * @throws SyncError if search fails
   */
  private async findFileByName(filename: string): Promise<string | null> {
    // Check cache first
    if (this.fileIdCache.has(filename)) {
      return this.fileIdCache.get(filename) || null;
    }

    // Not in cache, search Drive via Edge Function proxy
    if (!this.appFolderId) {
      return null;
    }

    const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
      body: {
        operation: 'search-file',
        params: { filename, folderId: this.appFolderId },
      },
    });

    if (error) {
      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        `Failed to search for file: ${error.message}`
      );
    }

    if (data && data.files && data.files.length > 0) {
      const fileId = data.files[0]?.id || null;

      // Update cache
      if (fileId) {
        this.fileIdCache.set(filename, fileId);
      }

      return fileId;
    }

    return null;
  }

  /**
   * Create new file in Google Drive using multipart upload
   *
   * Uploads file metadata and content in single request.
   *
   * @param filename - Name of file to create
   * @param content - File contents as string
   * @returns Promise resolving to new file ID
   * @throws SyncError if creation fails
   */
  private async createFile(filename: string, content: string, folderId: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
      body: {
        operation: 'upload-file',
        params: {
          filename,
          content,
          folderId,
        },
      },
    });

    if (error) {
      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        `Failed to create file: ${error.message}`
      );
    }

    if (!data || !data.id) {
      throw this.createSyncError(
        new Error('No file ID in response'),
        'UNKNOWN_ERROR',
        'Google Drive did not return file ID'
      );
    }

    return data.id;
  }

  /**
   * Update existing file in Google Drive using media upload
   *
   * Overwrites file content (metadata unchanged).
   *
   * @param fileId - Google Drive file ID
   * @param content - New file contents as string
   * @returns Promise resolving when update completes
   * @throws SyncError if update fails
   */
  private async updateFile(fileId: string, content: string): Promise<void> {
    const { error } = await supabase.functions.invoke('google-drive-proxy', {
      body: {
        operation: 'update-file',
        params: {
          fileId,
          content,
        },
      },
    });

    if (error) {
      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        `Failed to update file: ${error.message}`
      );
    }
  }

  /**
   * Override clearTokens to also clear GoogleDrive-specific caches
   */
  protected override async clearTokens(): Promise<void> {
    // Call parent to handle BFF disconnect
    await super.clearTokens();

    // Clear GoogleDrive-specific caches
    this.connectedCache = null;
    this.connectedCacheTs = 0;
    this.fileIdCache.clear();
  }

  // ===== BASE64 UTILITIES =====

  /** Base64 character lookup table */
  private static readonly BASE64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  /**
   * Convert ArrayBuffer to Base64 string
   *
   * Uses lookup table approach for binary-safe encoding (btoa is not safe for arbitrary bytes).
   *
   * @param buffer - ArrayBuffer to encode
   * @returns Base64-encoded string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const len = bytes.length;
    let result = '';

    for (let i = 0; i < len; i += 3) {
      const a = bytes[i] ?? 0;
      const b = i + 1 < len ? (bytes[i + 1] ?? 0) : 0;
      const c = i + 2 < len ? (bytes[i + 2] ?? 0) : 0;

      result += GoogleDriveSyncProvider.BASE64_CHARS[a >> 2];
      result += GoogleDriveSyncProvider.BASE64_CHARS[((a & 3) << 4) | (b >> 4)];
      result += i + 1 < len ? GoogleDriveSyncProvider.BASE64_CHARS[((b & 15) << 2) | (c >> 6)] : '=';
      result += i + 2 < len ? GoogleDriveSyncProvider.BASE64_CHARS[c & 63] : '=';
    }

    return result;
  }

  /**
   * Convert Base64 string to ArrayBuffer
   *
   * Uses lookup table approach for binary-safe decoding (atob is not safe for arbitrary bytes).
   *
   * @param base64 - Base64-encoded string
   * @returns Decoded ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    // Strip whitespace first (keep padding for validation)
    const trimmed = base64.replace(/\s/g, '');

    // Validate length is multiple of 4
    if (trimmed.length % 4 !== 0) {
      throw new Error('Invalid Base64: length must be multiple of 4');
    }

    // Validate padding is only at end
    const paddingMatch = trimmed.match(/=+$/);
    const paddingLen = paddingMatch ? paddingMatch[0].length : 0;
    const withoutPadding = trimmed.slice(0, trimmed.length - paddingLen);

    if (withoutPadding.includes('=')) {
      throw new Error('Invalid Base64: padding character in wrong position');
    }

    // Now strip padding for decoding
    const cleaned = withoutPadding;
    const len = cleaned.length;

    // Calculate output length (3 bytes per 4 Base64 chars, minus padding bytes)
    const outputLen = Math.floor(len * 3 / 4);
    const bytes = new Uint8Array(outputLen);

    let byteIndex = 0;
    for (let i = 0; i < len; i += 4) {
      const char0 = cleaned.charAt(i);
      const char1 = cleaned.charAt(i + 1);
      const char2 = i + 2 < len ? cleaned.charAt(i + 2) : '';
      const char3 = i + 3 < len ? cleaned.charAt(i + 3) : '';

      const a = GoogleDriveSyncProvider.BASE64_CHARS.indexOf(char0);
      const b = GoogleDriveSyncProvider.BASE64_CHARS.indexOf(char1);
      const c = char2 ? GoogleDriveSyncProvider.BASE64_CHARS.indexOf(char2) : 0;
      const d = char3 ? GoogleDriveSyncProvider.BASE64_CHARS.indexOf(char3) : 0;

      // Validate characters - indexOf returns -1 for invalid chars
      if (a === -1 || b === -1 || (char2 && c === -1) || (char3 && d === -1)) {
        throw new Error('Invalid Base64 character in input');
      }

      bytes[byteIndex++] = (a << 2) | (b >> 4);
      if (byteIndex < outputLen) bytes[byteIndex++] = ((b & 15) << 4) | (c >> 2);
      if (byteIndex < outputLen) bytes[byteIndex++] = ((c & 3) << 6) | d;
    }

    return bytes.buffer;
  }

  // ===== ERROR CODE MAPPING =====

  /**
   * Map edge function error code to SyncErrorCode
   *
   * @param code - Error code from edge function response
   * @returns Corresponding SyncErrorCode
   */
  private mapEdgeErrorToSyncError(code: string): SyncErrorCode {
    switch (code) {
      case 'NOT_CONNECTED':
        return 'NOT_CONNECTED';
      case 'PERMISSION_DENIED':
        return 'PERMISSION_DENIED';
      case 'FILE_NOT_FOUND':
        return 'FILE_NOT_FOUND';
      case 'FOLDER_MISSING':
        // Maps to UNKNOWN_ERROR but will trigger withSelfHeal() via isFolderError()
        return 'UNKNOWN_ERROR';
      case 'PROVIDER_RATE_LIMITED':
      case 'TRANSIENT':
        return 'NETWORK_ERROR';
      case 'QUOTA_EXCEEDED':
        return 'QUOTA_EXCEEDED';
      case 'INVALID_FILE':
        return 'INVALID_FILE';
      default:
        return 'UNKNOWN_ERROR';
    }
  }

  /**
   * Get error message for edge function errors
   *
   * For FOLDER_MISSING, returns a message that triggers isFolderError() detection
   * via the message.includes('folder') && message.includes('missing') check.
   *
   * @param edgeCode - Error code from edge function
   * @param edgeMessage - Original message from edge function
   * @param fallback - Fallback message if edge message is empty
   * @returns Error message that preserves self-heal behavior
   */
  private getEdgeErrorMessage(edgeCode: string | undefined, edgeMessage: string | undefined, fallback: string): string {
    // FOLDER_MISSING needs special message for isFolderError() to trigger self-heal
    if (edgeCode === 'FOLDER_MISSING') {
      return 'App folder missing - will retry';
    }
    return edgeMessage || fallback;
  }

  // ===== BINARY FILE OPERATIONS =====

  /**
   * Read binary file contents from images subfolder in Google Drive
   *
   * @param filename - Name of file to read (e.g., 'abc123.webp')
   * @returns Promise resolving to file contents as ArrayBuffer
   * @throws SyncError with FILE_NOT_FOUND if file or images folder doesn't exist
   * @throws SyncError with NOT_CONNECTED if not connected
   * @throws SyncError with PERMISSION_DENIED if access denied
   */
  async readBinary(filename: string): Promise<ArrayBuffer> {
    return this.withSelfHeal(async () => {
      await this.ensureValidToken();
      const folderId = await this.ensureAppFolder();

      console.log(`📥 Downloading binary file: ${filename}`);

      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: {
          operation: 'download-binary-file',
          params: { filename, folderId },
        },
      });

      // Handle invoke error (network, auth, etc.)
      if (error) {
        throw this.createSyncError(
          error,
          'UNKNOWN_ERROR',
          `Failed to download binary file: ${error.message}`
        );
      }

      // Handle edge function error response
      if (!data?.ok) {
        const errorCode = this.mapEdgeErrorToSyncError(data?.code || 'UNKNOWN_ERROR');
        const errorMessage = this.getEdgeErrorMessage(data?.code, data?.message, `Failed to download binary file: ${filename}`);
        throw this.createSyncError(
          new Error(errorMessage),
          errorCode,
          errorMessage
        );
      }

      // Decode Base64 response to ArrayBuffer
      const arrayBuffer = this.base64ToArrayBuffer(data.data);
      console.log(`✅ Downloaded binary file: ${filename} (${arrayBuffer.byteLength} bytes)`);

      return arrayBuffer;
    }, 'readBinary');
  }

  /**
   * Write binary file contents to images subfolder in Google Drive
   *
   * Creates the images subfolder if it doesn't exist.
   * Creates new file if it doesn't exist, overwrites if it does.
   *
   * @param filename - Name of file to write (e.g., 'abc123.webp')
   * @param data - File contents as ArrayBuffer
   * @param mimeType - Optional MIME type (defaults to 'application/octet-stream')
   * @returns Promise resolving when write completes
   * @throws SyncError with NOT_CONNECTED if not connected
   * @throws SyncError with PERMISSION_DENIED if access denied
   * @throws SyncError with QUOTA_EXCEEDED if storage limit reached
   */
  async writeBinary(filename: string, data: ArrayBuffer, mimeType?: string): Promise<void> {
    return this.withSelfHeal(async () => {
      await this.ensureValidToken();
      const folderId = await this.ensureAppFolder();

      console.log(`📤 Uploading binary file: ${filename} (${data.byteLength} bytes)`);

      // Default MIME type if not provided
      const effectiveMimeType = mimeType || 'application/octet-stream';

      // Encode ArrayBuffer to Base64
      const base64Data = this.arrayBufferToBase64(data);

      const { data: responseData, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: {
          operation: 'upload-binary-file',
          params: {
            filename,
            data: base64Data,
            mimeType: effectiveMimeType,
            folderId,
          },
        },
      });

      // Handle invoke error (network, auth, etc.)
      if (error) {
        throw this.createSyncError(
          error,
          'UNKNOWN_ERROR',
          `Failed to upload binary file: ${error.message}`
        );
      }

      // Handle edge function error response
      if (!responseData?.ok) {
        const errorCode = this.mapEdgeErrorToSyncError(responseData?.code || 'UNKNOWN_ERROR');
        const errorMessage = this.getEdgeErrorMessage(responseData?.code, responseData?.message, `Failed to upload binary file: ${filename}`);
        throw this.createSyncError(
          new Error(errorMessage),
          errorCode,
          errorMessage
        );
      }

      console.log(`✅ Uploaded binary file: ${filename}`);
    }, 'writeBinary');
  }

  /**
   * Delete binary file from images subfolder in Google Drive
   *
   * Idempotent: returns silently if file or images folder doesn't exist.
   *
   * @param filename - Name of file to delete (e.g., 'abc123.webp')
   * @returns Promise resolving when deletion completes
   * @throws SyncError with NOT_CONNECTED if not connected
   * @throws SyncError with PERMISSION_DENIED if access denied
   */
  async deleteBinary(filename: string): Promise<void> {
    return this.withSelfHeal(async () => {
      await this.ensureValidToken();
      const folderId = await this.ensureAppFolder();

      console.log(`🗑️ Deleting binary file: ${filename}`);

      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: {
          operation: 'delete-binary-file',
          params: { filename, folderId },
        },
      });

      // Handle invoke error (network, auth, etc.)
      if (error) {
        throw this.createSyncError(
          error,
          'UNKNOWN_ERROR',
          `Failed to delete binary file: ${error.message}`
        );
      }

      // Handle edge function error response
      // FILE_NOT_FOUND is idempotent (delete of non-existent file is ok)
      // Other errors (PERMISSION_DENIED, NOT_CONNECTED, FOLDER_MISSING) should throw
      if (!data?.ok && data?.code !== 'FILE_NOT_FOUND') {
        const errorCode = this.mapEdgeErrorToSyncError(data?.code || 'UNKNOWN_ERROR');
        const errorMessage = this.getEdgeErrorMessage(data?.code, data?.message, `Failed to delete binary file: ${filename}`);
        throw this.createSyncError(
          new Error(errorMessage),
          errorCode,
          errorMessage
        );
      }

      console.log(`✅ Deleted binary file: ${filename}`);
    }, 'deleteBinary');
  }

  /**
   * Check if binary file exists in images subfolder in Google Drive
   *
   * @param filename - Name of file to check (e.g., 'abc123.webp')
   * @returns Promise resolving to true if file exists, false otherwise
   * @throws SyncError with NOT_CONNECTED if not connected
   * @throws SyncError with PERMISSION_DENIED if access denied
   */
  async existsBinary(filename: string): Promise<boolean> {
    return this.withSelfHeal(async () => {
      await this.ensureValidToken();
      const folderId = await this.ensureAppFolder();

      const { data, error } = await supabase.functions.invoke('google-drive-proxy', {
        body: {
          operation: 'exists-binary-file',
          params: { filename, folderId },
        },
      });

      // Handle invoke error (network, auth, etc.)
      if (error) {
        throw this.createSyncError(
          error,
          'UNKNOWN_ERROR',
          `Failed to check binary file existence: ${error.message}`
        );
      }

      // Handle edge function error response
      if (!data?.ok) {
        const errorCode = this.mapEdgeErrorToSyncError(data?.code || 'UNKNOWN_ERROR');
        const errorMessage = this.getEdgeErrorMessage(data?.code, data?.message, `Failed to check binary file existence: ${filename}`);
        throw this.createSyncError(
          new Error(errorMessage),
          errorCode,
          errorMessage
        );
      }

      return data.exists === true;
    }, 'existsBinary');
  }

}
